import type { Ref } from 'vue';
import type {
  JournalDetailDTO,
  JournalEditionShortDTO,
} from '~/shared/api/client/journals/journals.dto';
import { OG_SITE_NAME, OG_TYPE, getOgImageMeta } from '~/shared/lib/seo';
import { useOrigin } from '~/shared/lib/useOrigin';

const DESCRIPTION =
  'Журналы CRE: выпуски Commercial Real Estate, спецпроекты, гиды и книги о рынке коммерческой недвижимости.';

/**
 * A tab of the section, or one edition of it. `section` is the name of the tab
 * — the page is headed "Архив" as readily as "Журналы", and the tab title has
 * to follow.
 *
 * `canonical` is the address the section itself links to. An edition answers
 * under any one segment before its slug, the way cre.ru does, and all those
 * addresses have to point search engines at a single page.
 *
 * `description` is the one the editors set on the tab; the section default
 * stands in while it is empty
 */
export function useJournalSeo(
  edition: Ref<JournalEditionShortDTO | null>,
  section: Ref<string> = ref('Журналы'),
  canonical?: () => string,
  description?: () => string | null | undefined,
) {
  const route = useRoute();
  const origin = useOrigin();

  useHead(() => {
    const title = edition.value
      ? `${section.value} — ${edition.value.name}`
      : section.value;

    const canonicalPath = canonical?.();
    const content = description?.()?.trim() || DESCRIPTION;

    return {
      title,
      link: canonicalPath
        ? [{ rel: 'canonical', href: `${origin}${canonicalPath}` }]
        : [],
      meta: [
        { name: 'description', content },
        // The section is a placeholder for now — keep it out of the index
        { name: 'robots', content: 'noindex, follow' },
        { property: 'og:type', content: OG_TYPE },
        { property: 'og:url', content: `${origin}${route.path}` },
        { property: 'og:title', content: title },
        { property: 'og:description', content },
        { property: 'og:site_name', content: OG_SITE_NAME },
        ...getOgImageMeta(origin),
      ],
    };
  });
}

/**
 * An issue page: the title of the number, its short description when it has
 * one. Cover as og:image — it is the picture of this page, unlike the section
 * default
 */
export function useJournalIssueSeo(issue: Ref<JournalDetailDTO | null>) {
  const route = useRoute();
  const origin = useOrigin();

  useHead(() => {
    const title = issue.value
      ? `${issue.value.title} — ${issue.value.edition.name}`
      : 'Журналы';
    const description = issue.value?.short_description ?? DESCRIPTION;

    return {
      title,
      meta: [
        { name: 'description', content: description },
        // The section is a placeholder for now — keep it out of the index
        { name: 'robots', content: 'noindex, follow' },
        { property: 'og:type', content: OG_TYPE },
        { property: 'og:url', content: `${origin}${route.path}` },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:site_name', content: OG_SITE_NAME },
        ...getOgImageMeta(origin, issue.value?.cover?.file_url),
      ],
    };
  });
}
